import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Drawer,
  Grid,
  IconButton,
  MenuItem,
  Stack,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import PaidIcon from "@mui/icons-material/Paid";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import EditIcon from "@mui/icons-material/Edit";
import VisibilityIcon from "@mui/icons-material/Visibility";
import PageContainer from "../../../components/PageContainer";
import AppDataTable from "../../../components/AppDataTable";
import { useAuth } from "../../../context/AuthContext";
import { getClientes } from "../../../services/modules/Clientes";
import {
  actualizarServicioDigitalAsignacion,
  actualizarServicioDigitalCatalogo,
  crearServicioDigitalAsignacion,
  crearServicioDigitalCatalogo,
  desactivarServicioDigitalAsignacion,
  desactivarServicioDigitalCatalogo,
  getServicioDigitalCredenciales,
  getServiciosDigitalesAsignaciones,
  getServiciosDigitalesCatalogo,
  crearServicioDigitalCobro,
} from "../../../services/modules/ServiciosDigitales";

const pad2 = (n) => String(n).padStart(2, "0");

const addMonthsClamped = (yyyyMmDd, months) => {
  if (!yyyyMmDd || !months) return "";
  const [yStr, mStr, dStr] = String(yyyyMmDd).split("-");
  const y = Number(yStr);
  const m = Number(mStr);
  const d = Number(dStr);
  if (!Number.isFinite(y) || !Number.isFinite(m) || !Number.isFinite(d)) return "";
  const base = new Date(Date.UTC(y, m - 1, 1));
  base.setUTCMonth(base.getUTCMonth() + Number(months));
  const ty = base.getUTCFullYear();
  const tm = base.getUTCMonth(); // 0-based
  const lastDay = new Date(Date.UTC(ty, tm + 1, 0)).getUTCDate();
  const day = Math.min(d, lastDay);
  return `${ty}-${pad2(tm + 1)}-${pad2(day)}`;
};

const todayStr = () => new Date().toISOString().slice(0, 10);

const ServiciosDigitales = () => {
  const theme = useTheme();
  const isXs = useMediaQuery(theme.breakpoints.down("sm"));
  const { user } = useAuth();
  const isAdmin = String(user?.role || user?.rol || "").toLowerCase() === "admin";

  const [tab, setTab] = useState(0);

  // Datos base
  const [clientes, setClientes] = useState([]);
  const [catalogo, setCatalogo] = useState([]);

  // Asignaciones
  const [asignaciones, setAsignaciones] = useState([]);
  const [filters, setFilters] = useState({
    q: "",
    activos: "1",
    // vacío = no filtra por próximos a vencer
    proximos_dias: "",
    vencidos: "0",
  });

  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState(null);

  // Drawer Asignación
  const [drawerAsignOpen, setDrawerAsignOpen] = useState(false);
  const [touchPassword, setTouchPassword] = useState(false);
  const [touchLicencia, setTouchLicencia] = useState(false);
  const [asignForm, setAsignForm] = useState({
    id_asignacion: null,
    cliente: null,
    id_servicio_digital: "",
    tipo: "LICENCIA",
    equipo_nombre: "",
    correo: "",
    password: "",
    licencia_codigo: "",
    otro_dato: "",
    fecha_inicio: todayStr(),
    vigencia_meses: 12,
    activo: true,
  });

  // Drawer Catálogo
  const [drawerCatalogoOpen, setDrawerCatalogoOpen] = useState(false);
  const [catalogoForm, setCatalogoForm] = useState({
    id_servicio_digital: null,
    nombre: "",
    descripcion: "",
    activo: true,
  });

  // Dialog Credenciales
  const [credOpen, setCredOpen] = useState(false);
  const [credData, setCredData] = useState(null);

  // Dialog Cobro
  const [cobroOpen, setCobroOpen] = useState(false);
  const [cobroForm, setCobroForm] = useState({
    id_asignacion: null,
    metodo_pago: "EFECTIVO",
    monto: "",
    descripcion: "",
    id_caja: 1,
  });

  const fetchBase = async () => {
    setLoading(true);
    setError(null);
    try {
      const [clientesData, catalogoData] = await Promise.all([
        getClientes(),
        getServiciosDigitalesCatalogo({ activos: "1" }),
      ]);
      setClientes(Array.isArray(clientesData) ? clientesData : []);
      setCatalogo(Array.isArray(catalogoData) ? catalogoData : []);
    } catch (e) {
      setError(e?.response?.data?.message || e?.message || "Error al cargar datos base.");
    } finally {
      setLoading(false);
    }
  };

  const fetchAsignaciones = async (override = null) => {
    setLoading(true);
    setError(null);
    try {
      const params = override || filters;
      const vencidosOn = params.vencidos === "1";
      const proximosNum = Number(params.proximos_dias);
      const hasProximos = Number.isFinite(proximosNum) && proximosNum > 0;
      const data = await getServiciosDigitalesAsignaciones({
        q: params.q || "",
        activos: params.activos,
        proximos_dias: !vencidosOn && hasProximos ? proximosNum : undefined,
        vencidos: vencidosOn ? "1" : "0",
      });
      setAsignaciones(Array.isArray(data) ? data : []);
    } catch (e) {
      setError(e?.response?.data?.message || e?.message || "Error al cargar asignaciones.");
      setAsignaciones([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchCatalogo = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await getServiciosDigitalesCatalogo({ activos: "1" });
      setCatalogo(Array.isArray(data) ? data : []);
    } catch (e) {
      setError(e?.response?.data?.message || e?.message || "Error al cargar catálogo.");
      setCatalogo([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBase().then(() => fetchAsignaciones());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const openAsignDrawer = (row = null) => {
    setError(null);
    setTouchPassword(false);
    setTouchLicencia(false);

    if (row) {
      const clienteSel = clientes.find((c) => Number(c.id_cliente) === Number(row.id_cliente)) || null;
      setAsignForm({
        id_asignacion: row.id_asignacion,
        cliente: clienteSel,
        id_servicio_digital: String(row.id_servicio_digital || ""),
        tipo: row.tipo || "LICENCIA",
        equipo_nombre: row.equipo_nombre || "",
        correo: row.correo || "",
        password: "",
        licencia_codigo: "",
        otro_dato: row.otro_dato || "",
        fecha_inicio: String(row.fecha_inicio || todayStr()).slice(0, 10),
        vigencia_meses: Number(row.vigencia_meses || 12),
        activo: Boolean(row.activo),
      });
    } else {
      setAsignForm({
        id_asignacion: null,
        cliente: null,
        id_servicio_digital: "",
        tipo: "LICENCIA",
        equipo_nombre: "",
        correo: "",
        password: "",
        licencia_codigo: "",
        otro_dato: "",
        fecha_inicio: todayStr(),
        vigencia_meses: 12,
        activo: true,
      });
    }
    setDrawerAsignOpen(true);
  };

  const closeAsignDrawer = () => {
    if (processing) return;
    setDrawerAsignOpen(false);
  };

  const openCatalogoDrawer = (row = null) => {
    setError(null);
    if (row) {
      setCatalogoForm({
        id_servicio_digital: row.id_servicio_digital,
        nombre: row.nombre || "",
        descripcion: row.descripcion || "",
        activo: Boolean(row.activo),
      });
    } else {
      setCatalogoForm({
        id_servicio_digital: null,
        nombre: "",
        descripcion: "",
        activo: true,
      });
    }
    setDrawerCatalogoOpen(true);
  };

  const closeCatalogoDrawer = () => {
    if (processing) return;
    setDrawerCatalogoOpen(false);
  };

  const handleSaveAsignacion = async () => {
    setProcessing(true);
    setError(null);
    try {
      const payload = {
        id_cliente: asignForm.cliente?.id_cliente,
        id_servicio_digital: Number(asignForm.id_servicio_digital || 0) || null,
        tipo: asignForm.tipo,
        equipo_nombre: asignForm.equipo_nombre || null,
        otro_dato: asignForm.otro_dato || null,
        fecha_inicio: asignForm.fecha_inicio,
        vigencia_meses: Number(asignForm.vigencia_meses || 0),
        activo: asignForm.activo ? 1 : 0,
      };

      if (asignForm.tipo === "USUARIO") {
        payload.correo = asignForm.correo || null;
        if (touchPassword) payload.password = asignForm.password; // "" limpia
      } else {
        if (touchLicencia) payload.licencia_codigo = asignForm.licencia_codigo; // "" limpia
      }

      if (asignForm.id_asignacion) {
        await actualizarServicioDigitalAsignacion(asignForm.id_asignacion, payload);
      } else {
        await crearServicioDigitalAsignacion(payload);
      }
      setDrawerAsignOpen(false);
      await fetchAsignaciones();
    } catch (e) {
      setError(e?.response?.data?.message || e?.message || "Error al guardar asignación.");
    } finally {
      setProcessing(false);
    }
  };

  const handleDisableAsignacion = async (row) => {
    if (!row?.id_asignacion) return;
    if (!window.confirm("¿Desactivar esta asignación?")) return;
    setProcessing(true);
    setError(null);
    try {
      await desactivarServicioDigitalAsignacion(row.id_asignacion);
      await fetchAsignaciones();
    } catch (e) {
      setError(e?.response?.data?.message || e?.message || "Error al desactivar asignación.");
    } finally {
      setProcessing(false);
    }
  };

  const handleVerCredenciales = async (row) => {
    if (!row?.id_asignacion) return;
    setProcessing(true);
    setError(null);
    try {
      const data = await getServicioDigitalCredenciales(row.id_asignacion);
      setCredData(data);
      setCredOpen(true);
    } catch (e) {
      setError(e?.response?.data?.message || e?.message || "No se pudieron obtener credenciales.");
    } finally {
      setProcessing(false);
    }
  };

  const openCobro = (row) => {
    if (!row?.id_asignacion) return;
    const desc = `Cobro ${row.servicio_nombre}${row.equipo_nombre ? ` (${row.equipo_nombre})` : ""}`;
    setCobroForm({
      id_asignacion: row.id_asignacion,
      metodo_pago: "EFECTIVO",
      monto: "",
      descripcion: desc,
      id_caja: 1,
    });
    setCobroOpen(true);
  };

  const handleGuardarCobro = async () => {
    setProcessing(true);
    setError(null);
    try {
      await crearServicioDigitalCobro({
        id_asignacion: cobroForm.id_asignacion,
        metodo_pago: cobroForm.metodo_pago,
        monto: Number(cobroForm.monto),
        descripcion: cobroForm.descripcion || null,
        id_caja: Number(cobroForm.id_caja || 1),
      });
      setCobroOpen(false);
      await fetchAsignaciones();
    } catch (e) {
      setError(e?.response?.data?.message || e?.message || "Error al registrar cobro.");
    } finally {
      setProcessing(false);
    }
  };

  const handleSaveCatalogo = async () => {
    setProcessing(true);
    setError(null);
    try {
      const payload = {
        nombre: catalogoForm.nombre,
        descripcion: catalogoForm.descripcion || null,
        activo: catalogoForm.activo ? 1 : 0,
      };
      if (catalogoForm.id_servicio_digital) {
        await actualizarServicioDigitalCatalogo(catalogoForm.id_servicio_digital, payload);
      } else {
        await crearServicioDigitalCatalogo(payload);
      }
      setDrawerCatalogoOpen(false);
      await fetchCatalogo();
      await fetchAsignaciones();
    } catch (e) {
      setError(e?.response?.data?.message || e?.message || "Error al guardar en catálogo.");
    } finally {
      setProcessing(false);
    }
  };

  const handleDisableCatalogo = async (row) => {
    if (!row?.id_servicio_digital) return;
    if (!window.confirm(`¿Desactivar "${row.nombre}" del catálogo?`)) return;
    setProcessing(true);
    setError(null);
    try {
      await desactivarServicioDigitalCatalogo(row.id_servicio_digital);
      await fetchCatalogo();
    } catch (e) {
      setError(e?.response?.data?.message || e?.message || "Error al desactivar del catálogo.");
    } finally {
      setProcessing(false);
    }
  };

  const venceChip = (dias) => {
    const d = Number(dias);
    if (!Number.isFinite(d)) return <Chip size="small" label="N/D" />;
    if (d < 0) return <Chip size="small" color="error" label={`Vencido (${d}d)`} />;
    if (d <= 7) return <Chip size="small" color="warning" label={`Urgente (${d}d)`} />;
    if (d <= 30) return <Chip size="small" color="warning" variant="outlined" label={`${d} días`} />;
    return <Chip size="small" color="success" variant="outlined" label={`${d} días`} />;
  };

  const asignColumns = useMemo(
    () => [
      {
        field: "cliente_nombre",
        headerName: "Cliente",
        flex: 1.3,
        minWidth: 220,
        sortable: true,
        valueGetter: (_v, r) => r?.cliente_nombre || "",
      },
      {
        field: "cliente_nit",
        headerName: "NIT",
        flex: 0.7,
        minWidth: 130,
        sortable: true,
        valueGetter: (_v, r) => r?.cliente_nit || "",
      },
      {
        field: "servicio_nombre",
        headerName: "Producto",
        flex: 1.1,
        minWidth: 200,
        sortable: true,
        valueGetter: (_v, r) => r?.servicio_nombre || "",
      },
      { field: "tipo", headerName: "Tipo", flex: 0.6, minWidth: 120, sortable: true },
      {
        field: "total_cobrado",
        headerName: "Cobrado",
        flex: 0.7,
        minWidth: 140,
        sortable: true,
        align: "right",
        headerAlign: "right",
        valueGetter: (_v, r) => Number(r?.total_cobrado || 0),
        renderCell: ({ row }) =>
          `Q ${Number(row.total_cobrado || 0).toLocaleString("es-GT", { minimumFractionDigits: 2 })}`,
      },
      {
        field: "equipo_nombre",
        headerName: "Equipo",
        flex: 0.9,
        minWidth: 160,
        sortable: true,
        valueGetter: (_v, r) => r?.equipo_nombre || "",
      },
      {
        field: "fecha_vencimiento",
        headerName: "Vence",
        flex: 0.8,
        minWidth: 150,
        sortable: true,
        valueGetter: (_v, r) => String(r?.fecha_vencimiento || ""),
      },
      {
        field: "estado",
        headerName: "Estado",
        flex: 0.7,
        minWidth: 150,
        sortable: true,
        valueGetter: (_v, r) => Number(r?.dias_para_vencer ?? 999999),
        renderCell: ({ row }) => venceChip(row.dias_para_vencer),
      },
      {
        field: "acciones",
        headerName: "Acciones",
        minWidth: 220,
        sortable: false,
        filterable: false,
        disableColumnMenu: true,
        renderCell: ({ row }) => (
          <Stack direction="row" spacing={1}>
            <Tooltip title="Editar">
              <IconButton size="small" onClick={() => openAsignDrawer(row)}>
                <EditIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Registrar cobro">
              <IconButton size="small" color="success" onClick={() => openCobro(row)}>
                <PaidIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            {isAdmin && (row.tiene_password || row.tiene_licencia) && (
              <Tooltip title="Ver credenciales">
                <IconButton size="small" onClick={() => handleVerCredenciales(row)}>
                  <VisibilityIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
            <Tooltip title="Desactivar">
              <IconButton size="small" color="error" onClick={() => handleDisableAsignacion(row)}>
                <DeleteOutlineIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Stack>
        ),
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [clientes, isAdmin]
  );

  const catalogoColumns = useMemo(
    () => [
      { field: "nombre", headerName: "Servicio", flex: 1.3, minWidth: 220, sortable: true },
      {
        field: "descripcion",
        headerName: "Descripción",
        flex: 1.8,
        minWidth: 260,
        sortable: false,
        valueGetter: (_v, r) => r?.descripcion || "",
      },
      {
        field: "activo",
        headerName: "Activo",
        flex: 0.6,
        minWidth: 120,
        sortable: true,
        valueGetter: (_v, r) => (r?.activo ? 1 : 0),
        renderCell: ({ row }) => (row.activo ? "Sí" : "No"),
      },
      {
        field: "acciones",
        headerName: "Acciones",
        minWidth: 180,
        sortable: false,
        filterable: false,
        disableColumnMenu: true,
        renderCell: ({ row }) => (
          <Stack direction="row" spacing={1}>
            <Tooltip title="Editar">
              <IconButton size="small" onClick={() => openCatalogoDrawer(row)}>
                <EditIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Desactivar">
              <IconButton size="small" color="error" onClick={() => handleDisableCatalogo(row)}>
                <DeleteOutlineIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Stack>
        ),
      },
    ],
    []
  );

  const vencimientoPreview = addMonthsClamped(asignForm.fecha_inicio, Number(asignForm.vigencia_meses || 0));

  return (
    <PageContainer>
      <Stack spacing={2}>
        <Box>
          <Typography variant="h5" fontWeight={800}>
            Servicios Digitales
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Asigna licencias o usuarios (Office, ESET, etc.) a clientes y controla vencimientos.
          </Typography>
        </Box>

        {error && <Alert severity="error">{error}</Alert>}

        <Tabs value={tab} onChange={(_e, v) => setTab(v)}>
          <Tab label="Asignaciones" />
          <Tab label="Catálogo" />
        </Tabs>

        {tab === 0 && (
          <Stack spacing={2}>
            <Stack
              direction={isXs ? "column" : "row"}
              spacing={2}
              justifyContent="space-between"
              alignItems={isXs ? "stretch" : "center"}
            >
              <Stack direction={isXs ? "column" : "row"} spacing={2} alignItems="center">
                <TextField
                  label="Buscar"
                  value={filters.q}
                  onChange={(e) => setFilters((p) => ({ ...p, q: e.target.value }))}
                  size="small"
                />
                <TextField
                  select
                  label="Activos"
                  size="small"
                  value={filters.activos}
                  onChange={(e) => setFilters((p) => ({ ...p, activos: e.target.value }))}
                  sx={{ minWidth: 140 }}
                >
                  <MenuItem value="1">Activos</MenuItem>
                  <MenuItem value="0">Inactivos</MenuItem>
                  <MenuItem value="all">Todos</MenuItem>
                </TextField>
                <TextField
                  type="number"
                  label="Próximos (días)"
                  size="small"
                  value={filters.proximos_dias}
                  onChange={(e) => setFilters((p) => ({ ...p, proximos_dias: e.target.value }))}
                  sx={{ maxWidth: 160 }}
                  inputProps={{ min: 1, max: 3650 }}
                  disabled={filters.vencidos === "1"}
                  helperText="Vacío = todos"
                />
                <TextField
                  select
                  label="Vencidos"
                  size="small"
                  value={filters.vencidos}
                  onChange={(e) =>
                    setFilters((p) => ({
                      ...p,
                      vencidos: e.target.value,
                    }))
                  }
                  sx={{ minWidth: 130 }}
                >
                  <MenuItem value="0">No</MenuItem>
                  <MenuItem value="1">Sí</MenuItem>
                </TextField>
                <Button variant="outlined" onClick={() => fetchAsignaciones()}>
                  Filtrar
                </Button>
              </Stack>

              <Button variant="contained" startIcon={<AddIcon />} onClick={() => openAsignDrawer(null)}>
                Nuevo
              </Button>
            </Stack>

            <AppDataTable
              rows={asignaciones}
              columns={asignColumns}
              loading={loading}
            />
          </Stack>
        )}

        {tab === 1 && (
          <Stack spacing={2}>
            <Stack
              direction={isXs ? "column" : "row"}
              spacing={2}
              justifyContent="space-between"
              alignItems={isXs ? "stretch" : "center"}
            >
              <Typography variant="body2" color="text.secondary">
                Productos digitales disponibles para asignar.
              </Typography>
              <Button variant="contained" startIcon={<AddIcon />} onClick={() => openCatalogoDrawer(null)} disabled={!isAdmin}>
                Nuevo producto
              </Button>
            </Stack>

            {!isAdmin && (
              <Alert severity="info">
                Solo <b>admin</b> puede crear/editar el catálogo.
              </Alert>
            )}

            <AppDataTable
              rows={catalogo}
              columns={catalogoColumns}
              loading={loading}
            />
          </Stack>
        )}
      </Stack>

      {/* Drawer Asignación */}
      <Drawer
        anchor="right"
        open={drawerAsignOpen}
        onClose={closeAsignDrawer}
        PaperProps={{ sx: { width: { xs: "100%", sm: 520 }, p: 2 } }}
      >
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Typography variant="h6" fontWeight={700}>
            {asignForm.id_asignacion ? "Editar asignación" : "Nueva asignación"}
          </Typography>
          <Button startIcon={<CloseIcon />} onClick={closeAsignDrawer} disabled={processing}>
            Cerrar
          </Button>
        </Stack>

        <Divider sx={{ my: 2 }} />

        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Autocomplete
              options={clientes}
              value={asignForm.cliente}
              onChange={(_e, v) => setAsignForm((p) => ({ ...p, cliente: v }))}
              getOptionLabel={(c) => `${c?.nit ? `${c.nit} - ` : ""}${c?.nombre || ""}`}
              renderInput={(params) => <TextField {...params} label="Cliente (NIT o nombre)" required />}
              isOptionEqualToValue={(a, b) => Number(a.id_cliente) === Number(b.id_cliente)}
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              select
              label="Producto"
              value={asignForm.id_servicio_digital}
              onChange={(e) => setAsignForm((p) => ({ ...p, id_servicio_digital: e.target.value }))}
              fullWidth
              required
            >
              {catalogo.map((s) => (
                <MenuItem key={s.id_servicio_digital} value={String(s.id_servicio_digital)}>
                  {s.nombre}
                </MenuItem>
              ))}
            </TextField>
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              select
              label="Tipo"
              value={asignForm.tipo}
              onChange={(e) => {
                const next = e.target.value;
                setAsignForm((p) => ({
                  ...p,
                  tipo: next,
                  correo: next === "USUARIO" ? p.correo : "",
                  password: "",
                  licencia_codigo: "",
                }));
                setTouchPassword(false);
                setTouchLicencia(false);
              }}
              fullWidth
            >
              <MenuItem value="LICENCIA">Licencia</MenuItem>
              <MenuItem value="USUARIO">Usuario</MenuItem>
            </TextField>
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              label="Equipo (opcional)"
              value={asignForm.equipo_nombre}
              onChange={(e) => setAsignForm((p) => ({ ...p, equipo_nombre: e.target.value }))}
              fullWidth
              placeholder="Ej: PC-Recepción"
            />
          </Grid>

          {asignForm.tipo === "USUARIO" ? (
            <>
              <Grid item xs={12}>
                <TextField
                  label="Correo (usuario)"
                  value={asignForm.correo}
                  onChange={(e) => setAsignForm((p) => ({ ...p, correo: e.target.value }))}
                  fullWidth
                  required
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label={asignForm.id_asignacion ? "Contraseña (dejar en blanco para no cambiar)" : "Contraseña"}
                  type="password"
                  value={asignForm.password}
                  onChange={(e) => {
                    setTouchPassword(true);
                    setAsignForm((p) => ({ ...p, password: e.target.value }));
                  }}
                  fullWidth
                />
              </Grid>
            </>
          ) : (
            <Grid item xs={12}>
              <TextField
                label={asignForm.id_asignacion ? "Código licencia (dejar en blanco para no cambiar)" : "Código licencia"}
                value={asignForm.licencia_codigo}
                onChange={(e) => {
                  setTouchLicencia(true);
                  setAsignForm((p) => ({ ...p, licencia_codigo: e.target.value }));
                }}
                fullWidth
                required={!asignForm.id_asignacion}
              />
            </Grid>
          )}

          <Grid item xs={12}>
            <TextField
              label="Otro / notas (opcional)"
              value={asignForm.otro_dato}
              onChange={(e) => setAsignForm((p) => ({ ...p, otro_dato: e.target.value }))}
              fullWidth
              placeholder="Ej: licencia para 3 PCs, link, etc."
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              type="date"
              label="Fecha inicio"
              value={asignForm.fecha_inicio}
              onChange={(e) => setAsignForm((p) => ({ ...p, fecha_inicio: e.target.value }))}
              fullWidth
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              type="number"
              label="Vigencia (meses)"
              value={asignForm.vigencia_meses}
              onChange={(e) => setAsignForm((p) => ({ ...p, vigencia_meses: e.target.value }))}
              fullWidth
              inputProps={{ min: 1, max: 1200 }}
            />
          </Grid>

          <Grid item xs={12}>
            <Alert severity="info">
              Vence: <b>{vencimientoPreview || "—"}</b>
            </Alert>
          </Grid>

          <Grid item xs={12}>
            <TextField
              select
              label="Activo"
              value={asignForm.activo ? 1 : 0}
              onChange={(e) => setAsignForm((p) => ({ ...p, activo: Number(e.target.value) === 1 }))}
              fullWidth
            >
              <MenuItem value={1}>Activo</MenuItem>
              <MenuItem value={0}>Inactivo</MenuItem>
            </TextField>
          </Grid>

          <Grid item xs={12}>
            <Button variant="contained" fullWidth onClick={handleSaveAsignacion} disabled={processing}>
              Guardar
            </Button>
          </Grid>
        </Grid>
      </Drawer>

      {/* Drawer Catálogo */}
      <Drawer
        anchor="right"
        open={drawerCatalogoOpen}
        onClose={closeCatalogoDrawer}
        PaperProps={{ sx: { width: { xs: "100%", sm: 520 }, p: 2 } }}
      >
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Typography variant="h6" fontWeight={700}>
            {catalogoForm.id_servicio_digital ? "Editar producto" : "Nuevo producto"}
          </Typography>
          <Button startIcon={<CloseIcon />} onClick={closeCatalogoDrawer} disabled={processing}>
            Cerrar
          </Button>
        </Stack>

        <Divider sx={{ my: 2 }} />

        <Grid container spacing={2}>
          <Grid item xs={12}>
            <TextField
              label="Nombre"
              value={catalogoForm.nombre}
              onChange={(e) => setCatalogoForm((p) => ({ ...p, nombre: e.target.value }))}
              fullWidth
              required
              disabled={!isAdmin}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              label="Descripción"
              value={catalogoForm.descripcion}
              onChange={(e) => setCatalogoForm((p) => ({ ...p, descripcion: e.target.value }))}
              fullWidth
              multiline
              minRows={3}
              disabled={!isAdmin}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              select
              label="Activo"
              value={catalogoForm.activo ? 1 : 0}
              onChange={(e) => setCatalogoForm((p) => ({ ...p, activo: Number(e.target.value) === 1 }))}
              fullWidth
              disabled={!isAdmin}
            >
              <MenuItem value={1}>Activo</MenuItem>
              <MenuItem value={0}>Inactivo</MenuItem>
            </TextField>
          </Grid>
          <Grid item xs={12}>
            <Button variant="contained" fullWidth onClick={handleSaveCatalogo} disabled={processing || !isAdmin}>
              Guardar
            </Button>
          </Grid>
        </Grid>
      </Drawer>

      {/* Credenciales dialog */}
      <Dialog open={credOpen} onClose={() => setCredOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Credenciales</DialogTitle>
        <DialogContent dividers>
          {credData ? (
            <Stack spacing={2} sx={{ mt: 1 }}>
              <Typography variant="body2" color="text.secondary">
                Equipo: {credData.equipo_nombre || "—"}
              </Typography>
              {credData.tipo === "USUARIO" ? (
                <>
                  <TextField label="Correo" value={credData.correo || ""} fullWidth InputProps={{ readOnly: true }} />
                  <TextField
                    label="Contraseña"
                    value={credData.password || ""}
                    fullWidth
                    InputProps={{ readOnly: true }}
                  />
                </>
              ) : (
                <TextField
                  label="Código licencia"
                  value={credData.licencia_codigo || ""}
                  fullWidth
                  InputProps={{ readOnly: true }}
                />
              )}
              <TextField
                label="Otro / notas"
                value={credData.otro_dato || ""}
                fullWidth
                InputProps={{ readOnly: true }}
              />
            </Stack>
          ) : (
            <Typography variant="body2" color="text.secondary">
              Sin datos.
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCredOpen(false)}>Cerrar</Button>
        </DialogActions>
      </Dialog>

      {/* Cobro dialog */}
      <Dialog open={cobroOpen} onClose={() => setCobroOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Registrar cobro</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              select
              label="Método de pago"
              value={cobroForm.metodo_pago}
              onChange={(e) => setCobroForm((p) => ({ ...p, metodo_pago: e.target.value }))}
              fullWidth
            >
              <MenuItem value="EFECTIVO">EFECTIVO</MenuItem>
              <MenuItem value="TRANSFERENCIA">TRANSFERENCIA</MenuItem>
              <MenuItem value="TARJETA">TARJETA</MenuItem>
              <MenuItem value="OTRO">OTRO</MenuItem>
            </TextField>

            <TextField
              type="number"
              label="Monto"
              value={cobroForm.monto}
              onChange={(e) => setCobroForm((p) => ({ ...p, monto: e.target.value }))}
              fullWidth
              inputProps={{ step: "0.01", min: 0 }}
              required
            />

            <TextField
              label="Descripción"
              value={cobroForm.descripcion}
              onChange={(e) => setCobroForm((p) => ({ ...p, descripcion: e.target.value }))}
              fullWidth
            />

            <TextField
              type="number"
              label="Caja (id)"
              value={cobroForm.id_caja}
              onChange={(e) => setCobroForm((p) => ({ ...p, id_caja: e.target.value }))}
              fullWidth
              inputProps={{ min: 1 }}
              helperText="Solo aplica si el método es EFECTIVO"
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCobroOpen(false)} disabled={processing}>
            Cancelar
          </Button>
          <Button variant="contained" onClick={handleGuardarCobro} disabled={processing}>
            Guardar
          </Button>
        </DialogActions>
      </Dialog>
    </PageContainer>
  );
};

export default ServiciosDigitales;
